import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { TextInput, FieldWarningWithIcon, Spinner } from '@td-client-frontend/common';
import { useMarketingProfileName } from '../../../hooks/useMarketingProfile';
import clsx from 'clsx';

interface ProfileName {
  minLength?: number;
  maxLength?: number;
  onValidName(profileName: string): void;
  onInvalidName(profileName: string): void;
  onChange?(name: string): void;
  profileName?: string;
}

export const ProfileName: FC<ProfileName> = ({
  minLength = 10,
  maxLength = 50,
  onValidName,
  onInvalidName,
  onChange,
  profileName = ''
}) => {
  const [searchTerm, setSearchTerm] = useState<string>(profileName);
  const [debouncedProfileNameSearch] = useDebounce<string>(searchTerm, 750);

  const {
    data: isAvailable,
    isFetching,
    isFetched
  } = useMarketingProfileName({
    search: debouncedProfileNameSearch
  });

  const hasValidLength: boolean =
    debouncedProfileNameSearch.length !== 0 &&
    debouncedProfileNameSearch.length >= minLength &&
    debouncedProfileNameSearch.length <= maxLength;
  const notAllowedCondition: boolean = hasValidLength && !isFetching && !isAvailable;
  const wrongLengthCondition: boolean = !!debouncedProfileNameSearch.length && !hasValidLength;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    if (onChange) onChange(debouncedProfileNameSearch);
  };

  // no isFetching means we have done with fetching
  // isFetched means query fetched data since the last query params changed
  const valueIsAvailable = !isFetching && isFetched && isAvailable;

  useEffect(() => {
    if (onChange) onChange(debouncedProfileNameSearch);
    if (valueIsAvailable) {
      onValidName(debouncedProfileNameSearch);
    } else {
      onInvalidName(debouncedProfileNameSearch);
    }
  }, [valueIsAvailable, debouncedProfileNameSearch, onChange, onValidName, onInvalidName]);

  return (
    <>
      <p className="text-lg text-gray-60">Profile name</p>
      <p className="text-15 leading-5 text-gray-60">
        {searchTerm.length}/{maxLength} characters
      </p>
      <TextInput
        name="profile_name_search_input"
        value={searchTerm}
        onChange={handleChange}
        maxLength={maxLength}
        wrapperClassName={clsx(
          'max-w-lg mt-2',
          (notAllowedCondition || wrongLengthCondition) && 'shadow-field-warning'
        )}
      />
      <div className="flex gap-x-2 items-center text-lg mt-2">
        {isFetching && (
          <>
            <Spinner size={16} />
            Checking name
          </>
        )}
        {notAllowedCondition && (
          <FieldWarningWithIcon message="The entered profile name already exists. Enter a unique name" />
        )}
        {wrongLengthCondition && (
          <FieldWarningWithIcon
            message={`The profile name needs to be between ${minLength} and ${maxLength} characters`}
          />
        )}
      </div>
    </>
  );
};
